import { FC, useMemo, useState } from 'react';
import { cn } from '@shadcn/ui/lib/utils';
import { ActionCard } from './action-card/action-card';
import { useQuery } from '@apollo/client';
import { addHours, subDays } from 'date-fns';
import { GET_CONTACTS_WEIGHTED_SCORES, GetContactsWeightedScoresQueryVariables } from '@zaplify/graphql';
import { mergeWeights, transformWeightsForQuery, UserObjectives, WeightKeys } from '@andsend/intelligence/shared';
import { ScrollArea } from '@shadcn/ui/components/ui/scroll-area';
import { useSnoozeDialogV2 } from '../../../../components/dialogs/snooze-dialog-v2';
import { AnimatePresence, motion } from 'motion/react';

// Constants - moved outside component to prevent recreation
const DEFAULT_LIMIT = 10;

export const ActionCardList: FC<{ className?: string }> = ({ className }) => {
    const [activeFilter, setActiveFilter] = useState<WeightKeys | 'urgent'>('urgent');

    // Constants - using useMemo for future database integration
    const ORGANIZATION_DEFAULTS = useMemo<UserObjectives>(
        () => ({ networking: 1.0, sales: 1.0 }),
        [] // Empty dependency array since these will eventually come from DB
    );

    const USER_SETTINGS = useMemo<UserObjectives>(() => ({ sales: 1.0, networking: 1.0 }), []);

    const CHAT_OVERRIDES = useMemo<UserObjectives>(() => ({ fundraising: 1.0 }), []);

    const HEURISTIC_BOOST = useMemo<UserObjectives>(() => ({ customer_success: 1.0 }), []);

    // Base weights - stable, never changes
    const baseWeights = useMemo(
        () => mergeWeights(ORGANIZATION_DEFAULTS, USER_SETTINGS, CHAT_OVERRIDES, HEURISTIC_BOOST),
        [] // Empty dependency array since all values are constants
    );

    const filterAdjustedWeights = useMemo(() => {
        const filterBoosts: Record<WeightKeys | 'urgent', UserObjectives> = {
            urgent: {
                urgent: 1,
                recruiting: 1,
                networking: 1,
                internal: 1,
                sales: 1,
                customer_success: 1,
                fundraising: 1,
            },
            networking: {
                networking: 2,
                sales: 0,
                internal: -1,
                recruiting: 0,
                customer_success: 0,
                fundraising: 0,
                urgent: 0,
            },
            sales: {
                sales: 2,
                networking: 0,
                internal: -1,
                recruiting: 0,
                customer_success: 0,
                fundraising: 0,
                urgent: 0,
            },
            customer_success: {
                customer_success: 2,
                sales: 0,
                internal: -1,
                recruiting: 0,
                networking: 0,
                fundraising: 0,
                urgent: 0,
            },
            fundraising: {
                fundraising: 1,
                networking: 0,
                internal: 0,
                recruiting: 0,
                sales: 0,
                customer_success: 0,
                urgent: 0,
            },
            recruiting: {
                recruiting: 1,
                networking: 0,
                internal: 0,
                sales: 0,
                customer_success: 0,
                fundraising: 0,
                urgent: 0,
            },
            internal: {
                internal: 1,
                customer_success: 0,
                sales: 0,
                networking: 0,
                recruiting: 0,
                fundraising: 0,
                urgent: 0,
            },
        };

        const result = mergeWeights(baseWeights, filterBoosts[activeFilter]);

        console.log('Filter weights recalculated:', {
            activeFilter,
            filterBoost: filterBoosts[activeFilter],
            baseWeights,
            result,
        });

        return result;
    }, [baseWeights, activeFilter]);

    const isOnChatRoute = location.pathname.includes('/actions-v2/chat');

    const queryVariables = useMemo(() => {
        const transformedWeights = transformWeightsForQuery(filterAdjustedWeights);

        console.log('Query variables:', {
            activeFilter,
            filterAdjustedWeights,
            transformedWeights,
        });

        const variables: GetContactsWeightedScoresQueryVariables = {
            ...transformedWeights,
            where: {
                _and: [
                    {
                        _or: [{ lastCompletedActionAt: { _is_null: true } }],
                    },
                    {
                        _or: [{ snoozedUntil: { _lt: addHours(new Date(), 8) } }, { snoozedUntil: { _is_null: true } }],
                    },
                ],

                compositeScore: { _gte: 0 },
                userSetAsNotInterestedAt: { _is_null: true },
            },
            limit: DEFAULT_LIMIT,
            offset: 0,
        };

        return variables;
    }, [filterAdjustedWeights]);

    // Main query for filtered and weighted contacts
    const { data, loading, error, fetchMore } = useQuery(GET_CONTACTS_WEIGHTED_SCORES, {
        variables: queryVariables,
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network', // Ensures refetch when variables change
        onCompleted: (data) => {
            console.log('Query completed with results:', {
                activeFilter,
                weights: filterAdjustedWeights,
                contactCount: data?.contacts_weighted_scores?.length,
            });
        },
    });

    const contacts = useMemo(() => {
        return data?.contacts_weighted_scores || [];
    }, [data]);

    const { SnoozeDialogV2Component, openDialogForContact } = useSnoozeDialogV2();

    // const contactsWithNormalizedScores = useMemo(() => {
    //     if (contacts.length === 0) return [];

    //     const scores = contacts.map((contact) => contact.compositeScore);
    //     const minScore = Math.min(...scores);
    //     const maxScore = Math.max(...scores);

    //     // Avoid division by zero if all scores are the same
    //     const scoreRange = maxScore - minScore;

    //     return contacts.map((contact) => {
    //         let normalizedScore: number;

    //         if (scoreRange === 0) {
    //             // If all scores are the same, assign 50 as the normalized score
    //             normalizedScore = 50;
    //         } else {
    //             // Normalize between 0-100
    //             normalizedScore = ((contact.compositeScore - minScore) / scoreRange) * 100;
    //         }

    //         return {
    //             contact,
    //             score: Math.round(normalizedScore),
    //         };
    //     });
    // }, [contacts]);

    return (
        <ScrollArea
            className={cn(
                'h-[calc(100dvh-6rem)] w-full',
                {
                    'h-[calc(100dvh-8rem)]': !isOnChatRoute,
                },
                className
            )}
        >
            <div className="flex flex-col gap-4 p-4 items-center justify-center w-full">
                <AnimatePresence>
                    {contacts.map((contact) => (
                        <motion.div
                            key={contact.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, x: -100 }}
                            transition={{ duration: 0.2 }}
                            className="w-full max-w-[min(70dvw,60rem)]"
                        >
                            <ActionCard
                                key={contact.id}
                                contact={contact}
                                score={contact.compositeScore}
                                openSnoozeDialogForContact={openDialogForContact}
                            />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            <SnoozeDialogV2Component />
        </ScrollArea>
    );
};
